// Export Data Modal Component
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as WordIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ExportDataService, { ExportDataFilters } from '../services/ExportDataService';
import { exportToPDF, exportToExcel, exportToWord } from '../utils/exportUtils';

interface ExportDataModalProps {
  open: boolean;
  onClose: () => void;
}

const ExportDataModal: React.FC<ExportDataModalProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data category filters
  const [filters, setFilters] = useState({
    includePatients: true,
    includeRevenue: true,
    includeDue: true,
    includeAppointments: true,
    includeAppointmentRevenue: true,
    includeRoomOccupancy: true,
    includeDischarges: true
  });
  
  // Time period
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | '3months' | '6months' | 'custom'>('month');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  
  // Export format
  const [exportFormat, setExportFormat] = useState<'pdf' | 'excel' | 'word'>('pdf');
  
  const handleFilterChange = (key: string) => {
    setFilters(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));
  };
  
  const handleExport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate custom date range
      if (dateRange === 'custom') {
        if (!customStartDate || !customEndDate) {
          setError('Please select both start and end dates for custom range');
          setLoading(false);
          return;
        }
        if (customStartDate > customEndDate) {
          setError('Start date must be before end date');
          setLoading(false);
          return;
        }
      }
      
      // Prepare export filters
      const exportFilters: ExportDataFilters = {
        dateRange,
        customStartDate: customStartDate || undefined,
        customEndDate: customEndDate || undefined,
        ...filters
      };
      
      console.log('🔄 Starting export with filters:', exportFilters);
      
      // Aggregate data
      const reportData = await ExportDataService.aggregateExportData(exportFilters);
      
      console.log('📊 Report data aggregated:', {
        patients: reportData.patients.length,
        appointments: reportData.appointments.length,
        payments: reportData.payments.length,
        totalRevenue: reportData.totalRevenue,
        totalDue: reportData.totalDue
      });
      
      // Generate export based on format
      switch (exportFormat) {
        case 'pdf':
          await exportToPDF(reportData, exportFilters);
          break;
        case 'excel':
          await exportToExcel(reportData, exportFilters);
          break;
        case 'word':
          await exportToWord(reportData, exportFilters);
          break;
      }
      
      console.log('✅ Export completed successfully');
      
      // Close modal after successful export
      setTimeout(() => {
        onClose();
      }, 500);
      
    } catch (err: any) {
      console.error('❌ Export error:', err);
      setError(err.message || 'Failed to export data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const isAnyFilterSelected = Object.values(filters).some(v => v);
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        backgroundColor: '#4A90E2', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DownloadIcon />
          <Typography variant="h6" component="div">Export Hospital Data</Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{ color: 'white', minWidth: 'auto' }}
          disabled={loading}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Data Categories */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 'bold' }}>
            📊 Select Data Categories
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.includePatients}
                  onChange={() => handleFilterChange('includePatients')}
                  color="primary"
                />
              }
              label="Total Patients (with detailed list)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.includeRevenue}
                  onChange={() => handleFilterChange('includeRevenue')}
                  color="primary"
                />
              }
              label="Total Revenue Generated"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.includeDue}
                  onChange={() => handleFilterChange('includeDue')}
                  color="primary"
                />
              }
              label="Pending Dues"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.includeAppointments}
                  onChange={() => handleFilterChange('includeAppointments')}
                  color="primary"
                />
              }
              label="Appointments Booked (with list)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.includeAppointmentRevenue}
                  onChange={() => handleFilterChange('includeAppointmentRevenue')}
                  color="primary"
                />
              }
              label="Appointment Revenue"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.includeRoomOccupancy}
                  onChange={() => handleFilterChange('includeRoomOccupancy')}
                  color="primary"
                />
              }
              label="Room Occupancy Statistics"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.includeDischarges}
                  onChange={() => handleFilterChange('includeDischarges')}
                  color="primary"
                />
              }
              label="Discharge Records"
            />
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Time Period */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 'bold' }}>
            📅 Select Time Period
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup value={dateRange} onChange={(e) => setDateRange(e.target.value as any)}>
              <FormControlLabel value="today" control={<Radio color="primary" />} label="Today" />
              <FormControlLabel value="week" control={<Radio color="primary" />} label="This Week (Last 7 days)" />
              <FormControlLabel value="month" control={<Radio color="primary" />} label="This Month" />
              <FormControlLabel value="3months" control={<Radio color="primary" />} label="Last 3 Months" />
              <FormControlLabel value="6months" control={<Radio color="primary" />} label="Last 6 Months" />
              <FormControlLabel value="custom" control={<Radio color="primary" />} label="Custom Date Range" />
            </RadioGroup>
          </FormControl>
          
          {/* Custom Date Range Pickers */}
          {dateRange === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={customStartDate}
                  onChange={(newValue) => setCustomStartDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
                <DatePicker
                  label="End Date"
                  value={customEndDate}
                  onChange={(newValue) => setCustomEndDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Box>
            </LocalizationProvider>
          )}
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Export Format */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 'bold' }}>
            📄 Select Export Format
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={exportFormat === 'pdf' ? 'contained' : 'outlined'}
              startIcon={<PdfIcon />}
              onClick={() => setExportFormat('pdf')}
              sx={{ flex: 1 }}
              color={exportFormat === 'pdf' ? 'error' : 'inherit'}
            >
              PDF
            </Button>
            <Button
              variant={exportFormat === 'excel' ? 'contained' : 'outlined'}
              startIcon={<ExcelIcon />}
              onClick={() => setExportFormat('excel')}
              sx={{ flex: 1 }}
              color={exportFormat === 'excel' ? 'success' : 'inherit'}
            >
              Excel
            </Button>
            <Button
              variant={exportFormat === 'word' ? 'contained' : 'outlined'}
              startIcon={<WordIcon />}
              onClick={() => setExportFormat('word')}
              sx={{ flex: 1 }}
              color={exportFormat === 'word' ? 'primary' : 'inherit'}
            >
              Word
            </Button>
          </Box>
        </Box>
        
        {/* Preview/Summary */}
        <Box sx={{ 
          p: 2, 
          backgroundColor: '#F7FAFC', 
          borderRadius: 2,
          border: '1px solid #E2E8F0'
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            📋 Export Summary:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • <strong>Data Categories:</strong> {Object.values(filters).filter(v => v).length} selected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • <strong>Time Period:</strong> {dateRange === 'custom' ? 'Custom Range' : dateRange}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • <strong>Format:</strong> {exportFormat.toUpperCase()}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          disabled={loading || !isAnyFilterSelected}
          sx={{ 
            backgroundColor: '#4A90E2',
            '&:hover': { backgroundColor: '#357ABD' }
          }}
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDataModal;
